/**
 * SystemWelcome — First-Visit Welcome Experience (Phase 9i)
 * V7: Navy curtain with logo + tagline, auto-dismiss after 3s.
 * Lifecycle gate preserved so AppLayout's guard still works.
 * Uses localStorage to skip on return visits.
 */

import { useState, useEffect } from 'react';
import { UI_INVARIANTS, WELCOME_TIMING, WELCOME_SIZES } from '../../config/ui-invariants';
import logoLarge from '../../assets/branding/logo-large.png';

const WELCOMED_KEY = 'accrezeo_welcomed';

export default function SystemWelcome() {
    // 9j: Always show welcome on every page load (session-based, not persisted)
    const [visible, setVisible] = useState(true);
    const [fadeOut, setFadeOut] = useState(false);

    useEffect(() => {
        if (!visible) {
            // Return visitor — complete lifecycle immediately
            UI_INVARIANTS.SESSION.setLifecycleComplete();
            return;
        }

        // First visit — show welcome, then dismiss
        const timer = setTimeout(() => {
            dismiss();
        }, 3000);

        return () => clearTimeout(timer);
    }, [visible]);

    function dismiss() {
        setFadeOut(true);
        localStorage.setItem(WELCOMED_KEY, 'true');
        setTimeout(() => {
            setVisible(false);
            UI_INVARIANTS.SESSION.setLifecycleComplete();
        }, WELCOME_TIMING.exitDuration);
    }

    if (!visible) return null;

    return (
        <div
            onClick={dismiss}
            className="fixed inset-0 z-[9999] flex flex-col items-center justify-center cursor-pointer bg-brand-900"
            style={{
                opacity: fadeOut ? 0 : 1,
                transition: `opacity ${WELCOME_TIMING.exitDuration}ms ease-out`,
            }}
        >
            {/* Logo — real brand asset, size from WELCOME_SIZES config */}
            <img
                src={logoLarge}
                alt="Accrezeo"
                width={WELCOME_SIZES.logoWidth}
                height={WELCOME_SIZES.logoHeight}
                className="mb-6"
                style={{
                    transform: fadeOut ? 'scale(0.88)' : 'scale(1)',
                    transition: `transform ${WELCOME_TIMING.logoDeblur}ms ease-out`,
                }}
            />

            {/* Brand name — size from WELCOME_SIZES config */}
            <h1
                className="text-white font-bold mb-3"
                style={{
                    fontFamily: UI_INVARIANTS.DS_TYPOGRAPHY.displayXL.fontFamily,
                    fontSize: `${WELCOME_SIZES.brandTextSize}px`,
                }}
            >
                Accrezeo
            </h1>

            {/* Tagline — size from WELCOME_SIZES config */}
            <p
                className="text-white/70 font-bold"
                style={{
                    fontSize: `${WELCOME_SIZES.taglineSize}px`,
                    opacity: fadeOut ? 0 : 1,
                    transition: `opacity ${WELCOME_TIMING.taglineFadeIn}ms ease-out`,
                }}
            >
                Creating value, one zero at a time
            </p>
        </div>
    );
}
